/**
 * Collapsible JSON viewer for task input/output.
 *
 * - Object/array nodes collapse with a click on the toggle.
 * - A search box filters keys + leaf values; matched paths stay open.
 * - A copy button serialises the whole node back to JSON.
 *
 * No syntax-highlight dep — leaf types get distinct text colours so the
 * eye still parses structure at a glance.
 */
import { useMemo, useState, type CSSProperties } from 'react';

import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { Input } from '../../ui/finixt.js';

interface JsonViewerProps {
  value: unknown;
  emptyText?: string;
  /** Expand every node on first render (default collapses past depth 2). */
  defaultExpanded?: boolean;
}

export function JsonViewer({
  value,
  emptyText = '(empty)',
  defaultExpanded = false,
}: JsonViewerProps) {
  const [search, setSearch] = useState('');
  const toast = useToast();
  const tree = useMemo(() => buildTree(value), [value]);
  const matches = useMemo(() => filterMatches(tree, search.trim().toLowerCase()), [tree, search]);

  if (value === null || value === undefined) {
    return (
      <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>{emptyText}</p>
    );
  }

  function copy() {
    void navigator.clipboard?.writeText(JSON.stringify(value, null, 2));
    toast.success('Copied');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter keys or values…"
          wrapperStyle={{ flex: '1 1 0%' }}
          style={{ height: 28, fontSize: 'var(--text-meta-lg)' }}
        />
        <Button size="sm" variant="ghost" onClick={copy} type="button">
          Copy JSON
        </Button>
      </div>
      <pre
        style={{
          overflow: 'auto',
          borderRadius: 'var(--radius-chip)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-sunken)',
          padding: 11,
          fontSize: 'var(--text-meta-lg)',
          lineHeight: 1.625,
        }}
        className="mono"
      >
        <Node node={tree} matches={matches} path="$" depth={0} defaultExpanded={defaultExpanded} />
      </pre>
    </div>
  );
}

interface TreeNode {
  kind: 'object' | 'array' | 'leaf';
  /** Keys for objects, indices-as-strings for arrays. */
  entries: { key: string; child: TreeNode }[];
  /** Stringified leaf, only when kind === 'leaf'. */
  leafText: string;
  /** Lowercase search haystack — keys + leaf text — for matching. */
  haystack: string;
  /** Raw value for the copy-subtree affordance. */
  raw: unknown;
}

function buildTree(value: unknown): TreeNode {
  if (value === null) return leaf('null', 'null');
  if (Array.isArray(value)) {
    const entries = value.map((v, i) => ({ key: String(i), child: buildTree(v) }));
    return {
      kind: 'array',
      entries,
      leafText: '',
      haystack: entries.map((e) => `${e.key} ${e.child.haystack}`).join(' '),
      raw: value,
    };
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value).map(([k, v]) => ({ key: k, child: buildTree(v) }));
    return {
      kind: 'object',
      entries,
      leafText: '',
      haystack: entries.map((e) => `${e.key.toLowerCase()} ${e.child.haystack}`).join(' '),
      raw: value,
    };
  }
  if (typeof value === 'string') return leaf(JSON.stringify(value), value.toLowerCase());
  return leaf(String(value), String(value).toLowerCase());
}

function leaf(text: string, haystack: string): TreeNode {
  return { kind: 'leaf', entries: [], leafText: text, haystack, raw: text };
}

/**
 * Walks the tree once and returns the set of node-paths that should stay
 * visible during a search. A parent stays visible when any descendant
 * matches, so the path to a hit is preserved.
 */
function filterMatches(tree: TreeNode, query: string): Set<string> | null {
  if (!query) return null;
  const out = new Set<string>();
  function walk(node: TreeNode, path: string): boolean {
    let hit = node.haystack.includes(query);
    for (const e of node.entries) {
      const childPath = `${path}.${e.key}`;
      if (walk(e.child, childPath)) hit = true;
    }
    if (hit) out.add(path);
    return hit;
  }
  walk(tree, '$');
  return out;
}

function Node({
  node,
  matches,
  path,
  depth,
  defaultExpanded,
  parentKey,
}: {
  node: TreeNode;
  matches: Set<string> | null;
  path: string;
  depth: number;
  defaultExpanded: boolean;
  parentKey?: string | undefined;
}) {
  const [open, setOpen] = useState(defaultExpanded || depth < 2);
  const visible = matches === null || matches.has(path);
  if (!visible) return null;

  if (node.kind === 'leaf') {
    return (
      <span style={leafColor(node.leafText)}>
        {parentKey !== undefined && <KeyLabel name={parentKey} />}
        {node.leafText}
      </span>
    );
  }

  const open$ = matches === null ? open : true;
  const opener = node.kind === 'array' ? '[' : '{';
  const closer = node.kind === 'array' ? ']' : '}';
  return (
    <span>
      {parentKey !== undefined && <KeyLabel name={parentKey} />}
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        aria-label={open$ ? 'Collapse' : 'Expand'}
        style={{ color: 'var(--text-muted)' }}
      >
        {opener}
        {!open$ && (
          <span
            style={{
              marginLeft: 5,
              marginRight: 5,
              fontSize: 'var(--text-meta-lg)',
              color: 'var(--text-muted)',
            }}
          >
            {node.entries.length} {node.kind === 'array' ? 'items' : 'keys'}
          </span>
        )}
        {!open$ && closer}
      </button>
      {open$ && (
        <>
          <div style={{ marginLeft: 16, display: 'flex', flexDirection: 'column' }}>
            {node.entries.map((e, i) => (
              <span key={e.key}>
                <Node
                  node={e.child}
                  matches={matches}
                  path={`${path}.${e.key}`}
                  depth={depth + 1}
                  defaultExpanded={defaultExpanded}
                  {...(node.kind === 'object' ? { parentKey: e.key } : {})}
                />
                {i < node.entries.length - 1 && (
                  <span style={{ color: 'var(--text-muted)' }}>,</span>
                )}
              </span>
            ))}
          </div>
          <span style={{ color: 'var(--text-muted)' }}>{closer}</span>
        </>
      )}
    </span>
  );
}

function KeyLabel({ name }: { name: string }) {
  return (
    <>
      <span style={{ color: 'var(--text-accent)' }}>&quot;{name}&quot;</span>
      <span style={{ color: 'var(--text-muted)' }}>: </span>
    </>
  );
}

function leafColor(text: string): CSSProperties {
  if (text === 'null') return { color: 'var(--text-muted)', fontStyle: 'italic' };
  if (text === 'true' || text === 'false') return { color: 'var(--warn)' };
  if (/^-?\d/.test(text)) return { color: 'var(--blue)' };
  return { color: 'var(--text-strong)' };
}
